import { type SupplierConfig, useSupplierReconciliation } from '@api/useSupplierReconciliation'

import type { SupplierConfigMetadataResponse } from '../../../../../api/useMetadata/types'
import { message } from 'antd'
import { useQueryClient } from '@tanstack/react-query'

export function useManageSupplierConfigs(metadata?: SupplierConfigMetadataResponse) {
  const { useUpsertConfig, useAddSupplierMapping, useToggleInvoiceHold } = useSupplierReconciliation()
  const queryClient = useQueryClient()

  const { mutateAsync: updateSupplierConfig } = useUpsertConfig()
  const { mutateAsync: addMapping } = useAddSupplierMapping()
  const { mutateAsync: toggleInvoiceHold } = useToggleInvoiceHold()

  async function updateEP(supplier: SupplierConfig) {
    if (!metadata) {
      message.error('No metadata found')
      return
    }

    // Handle assignee - if assignee_username is null/undefined, set assignee to null
    let assignee = null
    if (supplier.assignee_username) {
      const user = metadata.users.find((user) => user.username === supplier.assignee_username)
      if (user) {
        assignee = {
          username: user.username,
          name: user.name || user.username,
        }
      }
    }

    //TODO: backend is requiring empty object here when should support null.
    supplier.auto_approve_rules = supplier.auto_approve_rules
    supplier.auto_match_rules = supplier.auto_match_rules
    supplier.warning_rules = supplier.warning_rules

    try {
      const result = await updateSupplierConfig({
        body: {
          supplier_id: supplier.supplier_id,
          assignee: assignee,
          external_names: supplier.external_name_mappings || [],
          auto_match_rules: supplier.auto_match_rules,
          auto_approve_rules: supplier.auto_approve_rules,
          warning_rules: supplier.warning_rules,
        },
      })

      // Invalidate match overview queries to refresh MatchView when assignments change
      queryClient.invalidateQueries({
        queryKey: ['supplier_rec_match_overview'],
      })

      return result
    } catch (error: unknown) {
      console.error('Failed to update supplier config:', error)
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { status?: number; data?: { detail?: string } } }
        if (apiError.response?.status === 422) {
          const errorDetail = apiError.response?.data?.detail || 'Validation error'
          message.error(`Failed to update supplier: ${errorDetail}`)
        } else {
          message.error('Failed to update supplier configuration')
        }
      } else {
        message.error('Failed to update supplier configuration')
      }
      throw error // Re-throw to prevent grid from thinking it succeeded
    }
  }

  async function toggleSupplierHold(supplierId: string, hold: boolean) {
    return toggleInvoiceHold({
      params: {
        query: {
          supplier_id: supplierId,
          hold: hold,
        },
      },
    })
  }

  // Handle mapping external supplier
  async function handleMapSupplier(values: {
    source_name: string
    source_id?: string
    counterparty_id?: string
  }) {
    if (!values.counterparty_id) {
      throw new Error('Counterparty ID is required')
    }

    try {
      message.loading('Adding external supplier...', 0)
      const result = await addMapping({
        body: {
          source_name: values.source_name,
          supplier_id: values.counterparty_id,
        },
      })
      message.destroy()
      message.success('External supplier added successfully')
      return result
    } catch (error) {
      message.destroy()
      console.error('Failed to add external supplier:', error)
      message.error('Failed to add external supplier')
      throw error
    }
  }

  // Remove a mapping from a supplier
  async function removeMapping(supplier: SupplierConfig, mappingNames: string[]) {
    try {
      if (!supplier || !supplier.external_name_mappings) {
        message.error('Supplier data is invalid')
        return
      }

      // Filter out the mapping to remove
      const updatedMappings = (supplier.external_name_mappings || []).filter((name) => !mappingNames.includes(name))

      // Create updated supplier object
      const updatedSupplier = {
        ...supplier,
        external_name_mappings: updatedMappings,
      }

      // Update the supplier with filtered mappings
      // Handle assignee - if assignee_username is null/undefined, set assignee to null
      let assignee = null
      if (supplier.assignee_username) {
        const user = metadata?.users.find((user) => user.username === supplier.assignee_username)
        if (user) {
          assignee = {
            username: user.username,
            name: user.name || user.username,
          }
        }
      }

      await updateSupplierConfig({
        body: {
          supplier_id: updatedSupplier.supplier_id,
          assignee: assignee,
          external_names: updatedMappings,
          auto_match_rules: updatedSupplier.auto_match_rules,
          auto_approve_rules: updatedSupplier.auto_approve_rules,
          warning_rules: updatedSupplier.warning_rules,
        },
      })

      message.success(`Successfully removed ${mappingNames.length} mappings`)
      return true
    } catch (error) {
      message.error('Failed to remove mapping')
      console.error('Error removing mapping:', error)
      return false
    }
  }

  return {
    toggleSupplierHold,
    updateEP,
    handleMapSupplier,
    removeMapping,
  }
}
